/**
 * One task row (`docs/13-tasks-section-proposal.md` §2).
 *
 * The row is drawn from `TaskRow` alone — the contract of `tasks/T-T1.md` — and asks the server
 * for nothing else: item name (with the parent's name as monday's breadcrumb when the task is a
 * subitem), board chip, the person on the other side of the delegation, the status pill, the
 * due date, when it was assigned, and, on the delegation tab only, the **no board access** flag.
 *
 * The status pill is painted with the label's **own** colour: round T5 added
 * `TaskRow.statusColor`, the live hex out of the mapped status column's `labels_colors`, which
 * the read resolves once per board (P-267). A 20 px pill is mostly colour, so a grey one said
 * nothing. The text colour is derived from that hex by `readableTextOn` — the same formula
 * `StatusLabel` uses in the grid — so a label wearing `egg_yolk` keeps black text and one
 * wearing `stuck_red` keeps white. A row whose colour is `null` (no status mapping, a renamed
 * label, a colourless label) falls back to the neutral chip it had before.
 *
 * One deliberate absence, because the contract does not carry it and inventing it is forbidden
 * (CLAUDE.md rule 3): the row shows no group name. A task is (item, assignee); its group is
 * board chrome.
 */
import {
  Avatar,
  formatMondayDayMonth,
  formatMondayTime,
  readableTextOn,
  relativeTime,
} from '@elizmonday/ui';
import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { TASK_PERSON_COLUMN_LABELS } from './columns';
import type { TaskRowView, TaskTab } from './types';

/**
 * How long a finger has to stay still before the list enters bulk-select (round T4, F9-D32).
 *
 * 500 ms is the platform's own long-press: it is what Android's `ViewConfiguration`
 * (`DEFAULT_LONG_PRESS_TIMEOUT`) and iOS's `UILongPressGestureRecognizer` both default to, so a
 * person who long-presses anything else on their phone has already learned this gesture. Below
 * it a slow tap selects by accident; above it the gesture feels broken.
 */
export const TASK_LONG_PRESS_MS = 500;

/** A finger that travels this far was scrolling, not pressing. */
const LONG_PRESS_SLOP = 10;

export interface TaskRowProps {
  row: TaskRowView;
  tab: TaskTab;
  selected: boolean;
  /**
   * P-285 · `shift` is true when the box was clicked with Shift held, which the page turns into
   * a **range** from the last row toggled. It is read off the `click` — a `change` event carries
   * no modifier state at all — and `Space` on a focused box fires a click with `shift: false`,
   * so the keyboard path goes through exactly the same handler.
   */
  onToggleSelected(modifiers: { shift: boolean }): void;
  onOpenItem(): void;
  /**
   * **P-319** · this row's item is the one whose card is open **over** the list.
   *
   * It is not `selected`: selection is a tick the person made and drives the bulk bar, this is
   * "the card on the right is about this row" and drives nothing but the paint. Two different
   * states that can both be true at once, so they cannot share one attribute.
   */
  open?: boolean;
  /** Opens the mapped status column's editor over the pill. */
  onEditStatus(): void;
  /** Opens the mapped due column's editor over the date. */
  onEditDue(): void;
  onNudge(): void;
  onReassign(): void;
  /**
   * **P-301 §3** · the row's own archive. One handler, not two: the row already knows which way
   * it goes (`row.archivedAt`), and two props would let a caller wire the pair the wrong way
   * round on one of the three tabs.
   */
  onArchive(archive: boolean): void;
  /** The open editor, when this row owns it. */
  editor?: ReactNode;
  /** Which cell the open editor belongs to, so only that one is replaced. */
  editorRole?: 'status' | 'due' | 'assignee' | null;
  /**
   * Round T4 · the phone's bulk select. A long press on the row turns selection on and takes
   * this row with it; the page passes it only on a coarse pointer, so a desktop mouse that
   * rests on a row never changes the selection.
   */
  onLongPress?(): void;
}

/**
 * The pill's own paint (P-267): the board's label hex, with contrast-safe text on top.
 *
 * `undefined` — not an empty object — when the row carries no colour, so React leaves the
 * element's `style` attribute off entirely and the neutral CSS rule keeps its cascade.
 */
export function statusPillStyle(
  statusColor: string | null,
): { background: string; color: string } | undefined {
  if (statusColor === null) return undefined;
  return { background: statusColor, color: readableTextOn(statusColor) };
}

/** `15 Sep` or `15 Sep, 09:00` — the product's one date formatter (P-041). */
export function formatDue(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = formatMondayDayMonth(date);
  const midnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  return midnight ? day : `${day}, ${formatMondayTime(date)}`;
}

function initials(name: string | null): string {
  return (name ?? '?').slice(0, 2).toUpperCase();
}

export function TaskRow({
  row,
  tab,
  selected,
  onToggleSelected,
  onOpenItem,
  open = false,
  onEditStatus,
  onEditDue,
  onNudge,
  onReassign,
  onArchive,
  editor,
  editorRole,
  onLongPress,
}: TaskRowProps) {
  /** The delegation tabs show who it was given to; My Tasks shows who gave it. */
  const person = tab === 'assigned' ? row.assignee : row.assigner;
  /*
   * P-270 · the same word the column header prints, spoken into the row.
   *
   * A phone gets no header (`mobile.css` hides it), so on a phone this is the **only** thing that
   * says which side of the delegation the avatar is. It is set at every width all the same: the
   * desktop header is a `<div role="row">` beside a list of `<div>`s, not a `<table>`, so nothing
   * associates a header cell with the cell below it for a screen reader either.
   */
  const personLabel = TASK_PERSON_COLUMN_LABELS[tab];

  /*
   * The long press, with the two guards a real finger needs: a press that *moved* was a scroll,
   * and the click that follows a press which already fired must not also open the item — which
   * is the whole reason `fired` outlives the timer.
   */
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  /** Whether the click that is about to become a `change` was a Shift+click (P-285). */
  const shift = useRef(false);

  const cancelPress = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }, []);

  const startPress = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!onLongPress) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      cancelPress();
      fired.current = false;
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = window.setTimeout(() => {
        timer.current = null;
        fired.current = true;
        onLongPress();
      }, TASK_LONG_PRESS_MS);
    },
    [onLongPress, cancelPress],
  );

  const trackPress = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = origin.current;
      if (start === null) return;
      if (Math.abs(event.clientX - start.x) > LONG_PRESS_SLOP || Math.abs(event.clientY - start.y) > LONG_PRESS_SLOP) {
        cancelPress();
      }
    },
    [cancelPress],
  );

  return (
    <div
      className="ez-tasks__row"
      data-done={row.isDone || undefined}
      data-overdue={row.bucket === 'overdue' && !row.isDone ? '' : undefined}
      data-selected={selected || undefined}
      /* P-319 · the open card's row stays marked while the panel is up. */
      data-open={open || undefined}
      data-tab={tab}
      data-testid={`tasks-row-${row.id}`}
      onPointerDown={onLongPress ? startPress : undefined}
      onPointerMove={onLongPress ? trackPress : undefined}
      onPointerUp={onLongPress ? cancelPress : undefined}
      onPointerCancel={onLongPress ? cancelPress : undefined}
      onPointerLeave={onLongPress ? cancelPress : undefined}
      onClickCapture={
        onLongPress
          ? (event) => {
              if (!fired.current) return;
              fired.current = false;
              event.preventDefault();
              event.stopPropagation();
            }
          : undefined
      }
    >
      <label className="ez-tasks__select" aria-label={`Select ${row.itemName}`}>
        <input
          type="checkbox"
          checked={selected}
          data-testid={`tasks-row-select-${row.id}`}
          /*
           * The click only *records* the modifier; the change still performs the toggle. Doing
           * the work in `onClick` would toggle twice for a label click (the label re-dispatches
           * to the input) and not at all for a checkbox React re-rendered as controlled.
           */
          onClick={(event) => {
            shift.current = event.shiftKey;
          }}
          onChange={() => {
            onToggleSelected({ shift: shift.current });
            shift.current = false;
          }}
        />
      </label>

      <button
        type="button"
        className="ez-tasks__name-cell"
        data-testid={`tasks-row-open-${row.id}`}
        /* P-319 · the name is the control that opens the item card beside the list. */
        aria-expanded={open}
        onClick={onOpenItem}
      >
        {row.parentItemName === null ? null : (
          <nav className="ez-tasks__crumbs" aria-label="Breadcrumb">
            <span className="ez-tasks__crumb" data-testid={`tasks-row-breadcrumb-${row.id}`}>
              {row.parentItemName}
            </span>
          </nav>
        )}
        <span className="ez-tasks__name">{row.itemName}</span>
      </button>

      {/*
        Round T4. On a desktop this wrapper is `display: contents` and the row is exactly the
        seven-cell line it always was; on a phone it is the row's **second line**, which is what
        lets the name keep a whole line to itself inside 56 px (F9-D32).
      */}
      <span className="ez-tasks__meta">
        {/*
          P-270 · the chip keeps its own width; the **cell** around it is the column.
          `--ez-tasks-board-w` is what `.ez-tasks__colhead-board` reads too, which is what makes
          the word "Board" sit over the chips rather than near them. On a phone the cell is
          `display: contents` and the chip is a flex child of the meta line exactly as before.
        */}
        <span className="ez-tasks__board-cell">
          <span className="ez-tasks__sr">{'Board: '}</span>
          <span className="ez-tasks__board" data-testid={`tasks-row-board-${row.id}`} title={row.boardName}>
            {row.boardName}
          </span>
        </span>

        <span
          className="ez-tasks__person"
          role="group"
          aria-label={personLabel}
          data-person-label={personLabel}
          data-testid={`tasks-row-person-${row.id}`}
        >
          {person ? (
            <>
              <Avatar
                size="small"
                type={person.photoUrl ? 'img' : 'text'}
                src={person.photoUrl ?? undefined}
                text={initials(person.name)}
                aria-label={`${personLabel}: ${person.name ?? 'someone'}`}
              />
              <span className="ez-tasks__person-name">{person.name ?? person.id}</span>
            </>
          ) : (
            <span className="ez-tasks__person-name is-empty" title={`${personLabel}: nobody`}>
              —
            </span>
          )}
          {/*
            §4, the user's rule: the assigner always sees the delegation, flagged. The assignee
            never sees it at all, anywhere, so this can only ever appear on the `assigned` tab.
          */}
          {tab === 'assigned' && !row.assigneeHasAccess ? (
            <span
              className="ez-tasks__noaccess"
              data-testid={`tasks-row-noaccess-${row.id}`}
              title="This person cannot open the board, so the task does not appear in their Tasks, digests or notifications."
            >
              No board access
            </span>
          ) : null}
        </span>
      </span>

      <span className="ez-tasks__status-cell">
        {editorRole === 'status' && editor ? (
          editor
        ) : (
          <button
            type="button"
            className="ez-tasks__status"
            data-done={row.isDone || undefined}
            data-empty={row.statusLabel === null || undefined}
            data-colored={row.statusColor === null ? undefined : ''}
            aria-label={`Status: ${row.statusLabel ?? 'no status'}`}
            data-testid={`tasks-row-status-${row.id}`}
            onClick={onEditStatus}
          >
            <span
              className="ez-tasks__status-pill"
              data-colored={row.statusColor === null ? undefined : ''}
              data-status-color={row.statusColor ?? undefined}
              style={statusPillStyle(row.statusColor)}
            >
              {row.statusLabel ?? 'No status'}
            </span>
          </button>
        )}
      </span>

      <span className="ez-tasks__due-cell">
        {editorRole === 'due' && editor ? (
          editor
        ) : (
          <button
            type="button"
            className="ez-tasks__due"
            data-overdue={row.bucket === 'overdue' && !row.isDone ? '' : undefined}
            aria-label={`Due: ${row.dueAt === null ? 'no date' : formatDue(row.dueAt)}`}
            data-testid={`tasks-row-due-${row.id}`}
            onClick={onEditDue}
          >
            <span className="ez-tasks__due-chip">
              {row.dueAt === null ? 'No date' : formatDue(row.dueAt)}
            </span>
          </button>
        )}
      </span>

      <span className="ez-tasks__assigned" data-testid={`tasks-row-assigned-${row.id}`}>
        <span className="ez-tasks__sr">{'Assigned on: '}</span>
        {relativeTime(row.assignedAt, 'short')}
      </span>

      <span className="ez-tasks__row-actions">
        {tab === 'assigned' ? (
          <>
            <button
              type="button"
              className="ez-tasks__action"
              data-testid={`tasks-row-nudge-${row.id}`}
              onClick={onNudge}
            >
              Nudge
            </button>
            {editorRole === 'assignee' && editor ? (
              editor
            ) : (
              <button
                type="button"
                className="ez-tasks__action"
                data-testid={`tasks-row-reassign-${row.id}`}
                onClick={onReassign}
              >
                Reassign
              </button>
            )}
          </>
        ) : null}
        {/*
          P-301 §3 · the row's own half of the bulk bar, on every tab.
          **Which way it goes is the row's, not the tab's**: the Archive tab holds two kinds of
          row — the ones that are old and done (computed, `archivedAt === null`) and the ones
          somebody archived — and only the second kind has anything to unarchive. A computed row
          comes back by being reopened, so offering it *Unarchive* would be a button that
          truthfully reports "0 restored".
        */}
        {row.archivedAt === null ? (
          <button
            type="button"
            className="ez-tasks__action ez-tasks__action--archive"
            data-testid={`tasks-row-archive-${row.id}`}
            title="Take this task off your list. It stays on the board."
            onClick={() => onArchive(true)}
          >
            Archive
          </button>
        ) : (
          <button
            type="button"
            className="ez-tasks__action ez-tasks__action--archive"
            data-testid={`tasks-row-unarchive-${row.id}`}
            title="Put this task back in your list."
            onClick={() => onArchive(false)}
          >
            Unarchive
          </button>
        )}
      </span>
    </div>
  );
}
